<script setup>
import { ref, watchEffect } from 'vue';
import { getFromAPI } from '../../functionCollection';
import axios from 'axios';
import VueDatePicker from '@vuepic/vue-datepicker';

const props = defineProps(['fetchUrl', 'updateUrl']);

const submitData = ref({
    groupCounter: "",
    vertragsbeginn: null,
    vertragsende: null,
    send_invoice: false,
    invoice_email: "",
    bestellnummer: "",
});

const submitResult = ref(null);
const submitError = ref(null);
const validationErrors = ref({});
const isLoading = ref(false);

function getTodayDate() {
    const now = new Date();
    return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

watchEffect(async () => {
    submitResult.value = null;
    submitError.value = null;
    validationErrors.value = {};
    isLoading.value = true;

    try {
        const result = await getFromAPI(props.fetchUrl);
        submitData.value.groupCounter = result.GruppenzaehlerMax || "";
        submitData.value.vertragsbeginn = formatDateJS(result.vertragsbeginn);
        submitData.value.vertragsende = formatDateJS(result.vertragsende);
        submitData.value.send_invoice = result.send_invoice == 1;
        submitData.value.invoice_email = result.invoice_email || "";
        submitData.value.bestellnummer = result.bestellnummer || "";
    } catch (error) {
        console.error("Error fetching initial data:", error);
        submitError.value = "Fehler beim Laden der initialen Daten.";
        submitData.value = {
            groupCounter: "",
            vertragsbeginn: getTodayDate(),
            vertragsende: getTodayDate(),
            send_invoice: false,
            invoice_email: "",
            bestellnummer: "",
        };
    } finally {
        isLoading.value = false;
    }
});

function formatDateJS(dateString) {
    if (typeof dateString === "string" && dateString.length === 8) {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6);
        if (year && month && day && !isNaN(parseInt(year)) && !isNaN(parseInt(month)) && !isNaN(parseInt(day))) {
             return new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)));
        }
    }
    return getTodayDate();
}

async function submit() {
    submitError.value = null;
    submitResult.value = null;
    validationErrors.value = {};
    isLoading.value = true;

    const payload = {
        ...submitData.value,
        send_invoice: submitData.value.send_invoice ? 1 : 0,
    };

    axios.put(props.updateUrl, payload, { timeout: 6000 })
        .then(response => {
            if (response.status === 200 && response.data.status_code === 200) {
                submitResult.value = response.data;
            } else {
                 submitError.value = response.data.message || "Ein unerwarteter Fehler ist aufgetreten.";
                 if (response.data.errors) {
                    validationErrors.value = response.data.errors;
                 }
            }
            isLoading.value = false;
            setTimeout(() => { submitResult.value = null; submitError.value = null; }, 3500);
        })
        .catch(function (error) {
            isLoading.value = false;
            validationErrors.value = {};
            if (error.response && error.response.status === 422 && error.response.data.errors) {
                validationErrors.value = error.response.data.errors;
            } else if (error.code === 'ECONNABORTED') {
                submitError.value = "Server konnte nicht erreicht werden! Timeout. Überprüfen Sie Ihre Verbindung.";
            } else if (error.response) {
                submitError.value = `Fehler: ${error.response.status} - ${error.response.data.message || 'Serverfehler'}`;
            } else {
                submitError.value = "Ein Netzwerkfehler ist aufgetreten oder die Anfrage konnte nicht gesendet werden.";
                console.error("Submission Error:", error);
            }
             setTimeout(() => {
                submitResult.value = null;
                if (!Object.keys(validationErrors.value).length) {
                    submitError.value = null;
                }
             }, 5000);
        });
}
</script>

<template>
    <h3>Zusätzliche Benutzer Einstellungen</h3>
    <div class="well">
        <form @submit.prevent="submit">
            <div class="form-group row mb-3">
                <label for="groupCounterInput" class="col-sm-3 col-form-label">Max Gruppenzähler</label>
                <div class="col-sm-9">
                    <input
                        id="groupCounterInput"
                        type="number"
                        class="form-control"
                        v-model.number="submitData.groupCounter"
                        :class="{ 'is-invalid': validationErrors.groupCounter }"
                        aria-describedby="groupCounterFeedback"
                    />
                    <div id="groupCounterFeedback" class="invalid-feedback" v-if="validationErrors.groupCounter">
                        {{ validationErrors.groupCounter[0] }}
                    </div>
                </div>
            </div>
            <div class="form-group row mb-3">
                <div class="header" style="border-bottom: none; margin-left: -4px;">
                    <div class="checkbox-container">
                        <input type="checkbox" v-model="submitData.send_invoice">
                        <div class="check"></div>
                    </div>
                    <span>Automatischer Rechnungsversand</span>
                </div>
            </div>
            <div class="form-group row mb-3" v-if="submitData.send_invoice">
                <label for="invoiceEmailInput" class="col-sm-3 col-form-label">E-Mail für Rechnungsversand</label>
                <div class="col-sm-9">
                    <input
                        id="invoiceEmailInput"
                        type="email"
                        class="form-control"
                        v-model.trim="submitData.invoice_email"
                        placeholder="E-Mail-Adresse"
                        :class="{ 'is-invalid': validationErrors.invoice_email }"
                        aria-describedby="invoiceEmailFeedback"
                    />
                    <div id="invoiceEmailFeedback" class="invalid-feedback" v-if="validationErrors.invoice_email">
                        {{ validationErrors.invoice_email[0] }}
                    </div>
                </div>
            </div>
            <div class="form-group row mb-3">
                <label for="bestellnummerInput" class="col-sm-3 col-form-label">Bestellnummer für Rechnungen</label>
                <div class="col-sm-9">
                    <input
                        id="bestellnummerInput"
                        type="text"
                        class="form-control"
                        v-model.trim="submitData.bestellnummer"
                        maxlength="64"
                        :class="{ 'is-invalid': validationErrors.bestellnummer }"
                        aria-describedby="bestellnummerFeedback"
                        placeholder="Bestellnummer (max. 64 Zeichen)"
                    />
                    <div id="bestellnummerFeedback" class="invalid-feedback" v-if="validationErrors.bestellnummer">
                        {{ validationErrors.bestellnummer[0] }}
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                 <div class="col-md-6 mb-3 mb-md-0">
                     <div class="form-group">
                         <label for="vertragsbeginn" class="form-label">Vertragsbeginn</label>
                         <VueDatePicker
                            v-model="submitData.vertragsbeginn"
                            text-input
                            format="dd.MM.yyyy"
                            locale="de"
                            :enable-time-picker="false"
                            selectText="Auswählen"
                            cancelText="Abbrechen"
                            id="vertragsbeginn"
                            :input-class-name="validationErrors.vertragsbeginn ? 'form-control is-invalid' : 'form-control'"
                            :clearable="false"
                            auto-apply
                            utc
                            aria-describedby="vertragsbeginnFeedback"
                        />
                        <div id="vertragsbeginnFeedback" class="invalid-feedback" v-if="validationErrors.vertragsbeginn" style="display: block;">
                            {{ validationErrors.vertragsbeginn[0] }}
                        </div>
                     </div>
                 </div>
                 <div class="col-md-6">
                    <div class="form-group">
                         <label for="vertragsende" class="form-label">Vertragsende</label>
                         <VueDatePicker
                            v-model="submitData.vertragsende"
                            text-input
                            format="dd.MM.yyyy"
                            locale="de"
                            :enable-time-picker="false"
                            selectText="Auswählen"
                            cancelText="Abbrechen"
                            id="vertragsende"
                            :input-class-name="validationErrors.vertragsende ? 'form-control is-invalid' : 'form-control'"
                            :clearable="false"
                            auto-apply
                            utc
                            aria-describedby="vertragsendeFeedback"
                        />
                         <div id="vertragsendeFeedback" class="invalid-feedback" v-if="validationErrors.vertragsende" style="display: block;">
                            {{ validationErrors.vertragsende[0] }}
                        </div>
                    </div>
                 </div>
            </div>
            <div class="table-footer mt-3">
                <button class="btn btn-primary footer-button"
                    type="submit"
                    :disabled="isLoading">
                    <span v-if="!isLoading">
                        Speichern <i class="far fa-lg fa-save"></i>
                    </span>
                     <span v-else>
                         Speichern... <i class="fa-solid fa-spinner fa-spin"></i>
                     </span>
                </button>
            </div>
        </form>
        <div style="margin-top: 15px;">
            <div v-if="submitResult && submitResult.status_code === 200" class="alert alert-success">
                 {{ submitResult.message || 'Erfolgreich gespeichert!' }}
            </div>
             <div v-if="Object.keys(validationErrors).length > 0" class="alert alert-warning">
                  Bitte überprüfen Sie die markierten Felder.
             </div>
            <div v-if="submitError" class="alert alert-danger">
                 Daten konnten nicht gespeichert werden! <br> {{ submitError }}
            </div>
        </div>
    </div>
</template>
